import { useState, useEffect, useRef, useMemo } from "react";
import MarkdownRenderer from "react-markdown-renderer";
import "./airachat.css";
import {
  allSessionsData,
  generateChat,
  ragDocumentUpload,
  ragFileUpload,
} from "../../../Redux/chat/chat.action";
import { useDispatch, useSelector } from "react-redux";
import {
  formatDateForChatComponent,
  formatMarkdownResponse,
  isValidImageType,
} from "../../../utils/utils";
import axios from "axios";
import {
  POST_AIRA_AUDIO,
  POST_AIRA_SPEAKER,
  POST_TYPE_CHAT,
  POST_UPLOAD_PDF_FILE,
} from "../../../utils/api";
import { SpeakerCopyButton } from "./audioSpeaker";
import isFirstDayOfMonth from "date-fns/isFirstDayOfMonth";
import Loader from "../../../utils/loader/loader";
import ToastMessage from "../../../utils/toasts/toastMessage";

// ---------- helpers ----------
const isUserRole = (r) => r === "human" || r === "user";
const isAIRole = (r) => r === "ai" || r === "assistant";

const buildTurns = (messages = []) => {
  const turns = [];
  (messages || []).forEach((m) => {
    if (isUserRole(m?.role)) {
      turns.push({ id: m.id, question: m.content || "", answer: "" });
    } else if (isAIRole(m?.role)) {
      const last = turns[turns?.length - 1];
      if (last && !last.answer) last.answer = m.content || "";
      else turns.push({ id: m.id, question: "", answer: m.content || "" });
    }
  });
  return turns;
};

const startOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const groupSessionsByDate = (sessions = []) => {
  const groups = { today: [], yesterday: [], last7: [], older: [] };
  const todayStart = startOfDay(new Date());
  const DAY = 24 * 60 * 60 * 1000;

  [...(sessions || [])]
    .filter((s) => s?.created_at && s?.messages?.length > 0)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // newest first
    .forEach((s) => {
      const diffDays = Math.round(
        (todayStart - startOfDay(new Date(s.created_at))) / DAY,
      );
      if (diffDays <= 0) groups.today.push(s);
      else if (diffDays === 1) groups.yesterday.push(s);
      else if (diffDays <= 7) groups.last7.push(s);
      else groups.older.push(s);
    });

  return groups;
};

// sidebar title = first user message of the session
const getSessionTitle = (session) =>
  session?.messages?.find((m) => isUserRole(m?.role))?.content || "New chat";

const AiraChatBody = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [inputPrompt, setInputPrompt] = useState("");
  // turns coming from voice response / history sidebar
  const [sessionHistory, setSessionHistory] = useState([]);

  const [uploadedFilePreview, setUploadedFilePreview] = useState(null);
  const [resDocumentID, setResDocumentID] = useState(null);

  // current in-flight question (shown while waiting / streaming)
  const [pendingQuestion, setPendingQuestion] = useState(null);
  // FIX 1: turns frozen at send time, so previous chats stay visible while streaming
  const [turnsSnapshot, setTurnsSnapshot] = useState([]);
  // when true, the redux thread is hidden (new chat / opened old history)
  const [isNewChat, setIsNewChat] = useState(false);
  const lastTurnRef = useRef(null);
  const turnsAtSendRef = useRef(0); // number of turns when the question was sent
  const shouldScrollRef = useRef(false); // scroll once for each newly asked question

  // edit state
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState("");

  // mic recorder states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStarted, setRecordingStarted] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingStartedRef = useRef(false);

  const [sentAttachments, setSentAttachments] = useState({});
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);

  // history open state for mobile
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyTurns, setHistoryTurns] = useState([]); // turns of the chat opened from the sidebar

  const [imageFile, setImageFile] = useState(null);
  const [cachedSessions, setCachedSessions] = useState([]);

  const [threadID, setThreadID] = useState("");
  const abortControllerRef = useRef(null);

  const sendMessageRef = useRef(null);

  const storeMessages = useSelector(
    (state) => state?.ChatManager?.sessionData?.data?.messages,
  );
  const storeThreadId = useSelector(
    (state) => state?.ChatManager?.sessionData?.data?.thread_id,
  );

  const allSessionsStore = useSelector(
    (state) => state?.ChatManager?.allSessionsData?.data?.sessions,
  );

  const streamingText = useSelector(
    (state) => state?.ChatManager?.streamingText,
  );
  const isStreaming = useSelector((state) => state?.ChatManager?.streaming);

  useEffect(() => {
    dispatch(allSessionsData());
  }, [dispatch]);

  const handleAnswerLinkClick = async (e) => {
    const link = e.target.closest("a");
    const href = link?.getAttribute("href");
    if (!link || !href || href.startsWith("#")) return;

    e.preventDefault();

    // only send the token to your own API, never to external sites
    const apiOrigin = new URL(POST_TYPE_CHAT, window.location.origin).origin;
    const isApiLink = new URL(link.href).origin === apiOrigin;

    if (!isApiLink) {
      window.open(link.href, "_blank", "noopener,noreferrer");
      return;
    }

    try {
      const token = localStorage.getItem("access_token") || null;
      const res = await fetch(link.href, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);

      // filename from the server header, otherwise from the URL path
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const urlName = decodeURIComponent(
        new URL(link.href).pathname.split("/").filter(Boolean).pop() ||
          "download",
      );
      const filename = match ? decodeURIComponent(match[1]) : urlName;

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      setToastState({
        show: true,
        message: err?.message || "Unable to open the link.",
        type: "error",
      });
    }
  };

  // recording timer
  useEffect(() => {
    if (!isRecording) {
      setRecordingSeconds(0);
      return;
    }
    const id = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  // turns derived straight from redux (no local copy -> no flicker)
  const storeTurns = useMemo(
    () => (isNewChat ? [] : buildTurns(storeMessages)),
    [storeMessages, isNewChat],
  );

  const displayTurns =
    sessionHistory.length > 0
      ? sessionHistory
      : storeTurns.length > 0
        ? storeTurns
        : historyTurns;

  // remember the last non-empty list from redux
  useEffect(() => {
    if (Array.isArray(allSessionsStore) && allSessionsStore.length > 0) {
      setCachedSessions(allSessionsStore);
    }
  }, [allSessionsStore]);

  // use the store when it has data, otherwise the last known list
  const sessionsForHistory =
    Array.isArray(allSessionsStore) && allSessionsStore.length > 0
      ? allSessionsStore
      : cachedSessions;

  const historyGroups = useMemo(() => {
    const g = groupSessionsByDate(sessionsForHistory);
    return [
      { id: "today", label: "Today", items: g.today },
      { id: "yesterday", label: "Yesterday", items: g.yesterday },
      { id: "last7", label: "Last 7 days", items: g.last7 },
      { id: "older", label: "Older", items: g.older },
    ].filter((group) => group.items.length > 0);
  }, [sessionsForHistory]);

  // keep thread id in sync with redux
  useEffect(() => {
    if (storeThreadId && !isNewChat) setThreadID(storeThreadId);
  }, [storeThreadId, isNewChat]);

  // the in-flight block is shown until redux has the new turn
  const showPending =
    !!pendingQuestion && displayTurns?.length <= turnsAtSendRef.current;

  // FIX 1: while a question is in flight, keep rendering the turns that
  // existed when it was sent, even if redux clears/replaces them mid-stream
  const renderTurns = showPending ? turnsSnapshot : displayTurns;

  // scroll the current question to the top: on send, and again after redux
  // data arrives and the in-flight block is swapped for the real turn
  useEffect(() => {
    if (!shouldScrollRef.current) return;
    const el = lastTurnRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    if (!showPending) shouldScrollRef.current = false; // answer is in, done
  }, [showPending, displayTurns?.length, pendingQuestion]);

  useEffect(() => {
    const sections = Array.from(document.querySelectorAll("section[id]"));

    const scrollHandler = (entries) => {
      entries.forEach((entry) => {
        const section = entry.target;
        const sectionId = section.id;
        const sectionLink = document.querySelector(`a[href="#${sectionId}"]`);

        if (entry.intersectionRatio > 0) {
          section.classList.add("active");
          if (sectionLink) sectionLink.classList.add("active");
        } else {
          section.classList.remove("active");
          if (sectionLink) sectionLink.classList.remove("active");
        }
      });
    };

    const observer = new window.IntersectionObserver(scrollHandler);

    sections.forEach((section) => observer.observe(section));
    return () => {
      observer.disconnect();
    };
  }, []);

  const handleNewChat = () => {
    setIsNewChat(true);
    setThreadID("");
    setResDocumentID(null);
    setSessionHistory([]);
    setInputPrompt("");
    setPendingQuestion(null);
    setTurnsSnapshot([]); // FIX 1
    setEditingIndex(null);
    setSentAttachments({});
    setUploadedFilePreview(null);
    setImageFile(null);
    setHistoryTurns([]);
    localStorage.removeItem("material_id");
    localStorage.removeItem("session_id");
  };

  const handleSelectSession = (e, session) => {
    e.preventDefault();
    if (isStreaming) return; // don't switch chats mid-stream

    localStorage.removeItem("material_id");
    localStorage.setItem("session_id", session.thread_id || "");

    setIsNewChat(true); // hide the current redux thread
    setSessionHistory([]);
    setHistoryTurns(buildTurns(session.messages));
    setThreadID(session.thread_id || ""); // new messages continue this chat

    setPendingQuestion(null);
    setTurnsSnapshot([]);
    setEditingIndex(null);
    setSentAttachments({});
    setUploadedFilePreview(null);
    setImageFile(null);
    setResDocumentID(null);
    shouldScrollRef.current = false;
    setIsHistoryOpen(false); // close the mobile drawer
  };

  useEffect(() => {
    localStorage.removeItem("session_id");
    localStorage.removeItem("material_id");
    setSessionHistory([]);
  }, []);

  // ------------------------------- send / regenerate / edit -------------------------------------

  const handleRegenerate = (turn) => sendMessage(turn?.question);

  const handleEditSubmit = () => {
    if (!editText.trim()) return;
    setEditingIndex(null);
    sendMessage(editText); // appended as a new turn
  };

  const handleEditCancelBtn = () => {
    setEditingIndex(null);
  };

  //mic recorder-to-mp3

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]); // avoid rerender

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      audioChunksRef.current = []; // reset chunks

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data); // store in ref
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        audioChunksRef.current = [];
        if (audioBlob.size === 0) return; // nothing recorded

        const audioFile = new File([audioBlob], "voice.webm", {
          type: "audio/webm",
        });
        sendMessageRef.current("", { audioFile }); // mic_audio: true + mic_audio_file
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      setToastState({
        show: true,
        message: "Failed to start recording:",
        err,
        type: "error",
      });
    }
  };

  const stopRecording = () => {
    recordingStartedRef.current = true;
    setRecordingStarted(true);

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      try {
        mediaRecorderRef.current.stop(); // triggers onstop
      } catch (err) {
        setToastState({
          show: true,
          message: "MediaRecorder stop failed:",
          err,
          type: "error",
        });
      }
    }

    // Stop mic access
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (e) {
          // console.warn("Failed to stop track:", e);
        }
      });
      streamRef.current = null;
    }

    setIsRecording(false);
  };

  //Image and doc upload

  const sendToWhisper = async (blob) => {
    const formData = new FormData();

    const audioFile = new File([blob], "voice.webm", { type: blob.type });
    formData.append("audio_file", audioFile);
    formData.append("session_id", localStorage.getItem("session_id") || "");
    formData.append("history", []);

    const token = localStorage.getItem("access_token") || null;

    try {
      const response = await axios.post(POST_AIRA_AUDIO, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      const history = buildTurns(response?.data?.updated_history);
      recordingStartedRef.current = false;
      setRecordingStarted(false);
      setSessionHistory(history);
    } catch (error) {
      recordingStartedRef.current = false;
      setRecordingStarted(false);
      setToastState({
        show: true,
        message: "Whisper API error:",
        error,
        type: "error",
      });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isValidImageType(file)) {
      setToastState({
        show: true,
        message: "Unsupported image format",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    localStorage.removeItem("material_id");

    // Revoke the previous preview URL to avoid memory leaks
    if (uploadedFilePreview?.url) {
      URL.revokeObjectURL(uploadedFilePreview.url);
    }

    setImageFile(file);
    setUploadedFilePreview({
      type: "image",
      url: URL.createObjectURL(file),
      name: file.name,
    });

    const payload = { file };

    try {
      const result = await dispatch(ragDocumentUpload(payload));

      if (result?.success) {
        // setToastState({
        //   show: true,
        //   message: "Image uploaded! Ask your question",
        //   type: "success",
        // });
      } else {
        setToastState({
          show: true,
          message: result?.message || "Unsupported image format",
          type: "error",
        });
      }
    } catch (error) {
      setToastState({
        show: true,
        message: error?.message || "Something went wrong! Try again.",
        type: "error",
      });
    } finally {
      e.target.value = "";
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(null);
    setUploadedFilePreview({
      type: "doc",
      url: null,
      name: file.name,
    });

    const payload = { file }; // use `file`, not the stale `imageFile` state

    try {
      const result = await dispatch(ragDocumentUpload(payload));

      if (result?.success) {
        const documentID = result?.data?.document_id;
        setResDocumentID(documentID);
        // setToastState({
        //   show: true,
        //   message: "Image uploaded! Ask your question",
        //   type: "success",
        // });
      } else {
        setToastState({
          show: true,
          message: result?.message || "Unsupported image format",
          type: "error",
        });
      }
    } catch (error) {
      setToastState({
        show: true,
        message: error?.message || "Something went wrong! Try again.",
        type: "error",
      });
    } finally {
      e.target.value = "";
    }
  };

  const handleRemoveAttachment = () => {
    if (uploadedFilePreview?.url) URL.revokeObjectURL(uploadedFilePreview.url);
    setUploadedFilePreview(null);
    setImageFile(null);
    localStorage.removeItem("material_id");
    setResDocumentID(null);
  };

  const sendMessage = async (text, { audioFile = null } = {}) => {
    const messageText = (text || "").trim();
    if ((!messageText && !audioFile) || isStreaming) return;

    setInputPrompt("");
    turnsAtSendRef.current = displayTurns?.length;
    setTurnsSnapshot(displayTurns);
    shouldScrollRef.current = true;
    setPendingQuestion(messageText || "🎤 Voice message");

    // remember the attachment against the turn index it belongs to
    if (uploadedFilePreview) {
      setSentAttachments((prev) => ({
        ...prev,
        [displayTurns?.length]: uploadedFilePreview,
      }));
      setUploadedFilePreview(null);
      setImageFile(null);
    }

    abortControllerRef.current = new AbortController();

    const payload = {
      thread_id: threadID || null,
      documentID: resDocumentID || null,
      message: messageText,
      mic_audio: !!audioFile,
    };
    // only include the key when a recording exists
    if (audioFile) payload.mic_audio_file = audioFile;

    const result = await dispatch(
      generateChat(payload, { signal: abortControllerRef.current.signal }),
    );

    setPendingQuestion(null);

    if (result?.success) {
      setResDocumentID(null);
      setSessionHistory([]);
      setIsNewChat(false);
      if (result.threadId) setThreadID(result.threadId);
      dispatch(allSessionsData());
    } else if (!result?.aborted) {
      shouldScrollRef.current = false;
      setToastState({
        show: true,
        message: result?.message || "Something went wrong! Try again.",
        type: "error",
      });
    }
  };

  sendMessageRef.current = sendMessage; // always points to the latest render
  const handleSendPrompt = () => sendMessage(inputPrompt);

  return (
    <section className="aira-chat">
      <div className="chat-index">
        <div className="bdrs28 ts03 ad-block">
          <div className="ad-block__inner">
            <h3 className="fz28 ff-inter text-white fw600">
              8/10 Free Generations
            </h3>
            <button className="fw500 fz16 border-0 btn btn-solid btn-solid--white">
              Upgrade
            </button>
          </div>
        </div>
        <div
          className={`bdrs40 overflow-hidden chat-history ${isHistoryOpen ? "open-history" : ""}`}
        >
          <div className="mb-0 title-992">
            <h5 className="fw900 fz24">History</h5>
            <button className="btn btn-round btn-round--green js-close-btn">
              <img src="./assets/images/Group.png" alt="" />
            </button>
          </div>
 
          <div className="chat-history__list">
            {historyGroups?.length ? historyGroups?.map((group) => (
              <section
                key={group?.id}
                id={group?.id}
                className={`history-group ${group.id}-group`}
              >
                <p className="fz10 text-uppercase fw500 mb-2 ms-3 mt-3">
                  {group?.label}
                </p>
                <ul className="list-unstyled mb-3">
                  {group.items.map((data) => (
                    <li key={data.thread_id}>
                      <button
                        className={`btn border-0 history-info ${
                          threadID === data.thread_id ? "selected" : ""
                        }`}
                        onClick={(e) => handleSelectSession(e, data)}
                      >
                        <h6 className="fw500">{getSessionTitle(data)}</h6>
                        <p className="fz10 fw500">
                          {formatDateForChatComponent(data.created_at)}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )) :  <p className="fz10 fw500 mb-2 ms-3 mt-3">
                  Start a new chat to see your history here...
                </p>}
          </div>
        </div>
      </div>
      <div className="pb-0 bdrs30 chat-area chat-area--aira">
        <div className="title-row">
          <div className="title-row__left">
            <h2 className="fw900 fz28">Chat with AI</h2>
          </div>
          <div className="title-row__right" onClick={handleNewChat}>
            <button className="btn btn-solid bdrs20 btn-solid--dark">
              <img
                src="./assets/images/inner-pages/icons/icon-new-chat.svg"
                alt=""
              />
              <span className="fw500 text-white fz20">New chat</span>
            </button>
            <button
              className="btn btn-round btn-round--actions js-history-btn hide-above-768"
              onClick={() => setIsHistoryOpen(true)}
            >
              <img
                src="./assets/images/inner-pages/icons/icon-history-aira-chat.svg"
                alt=""
              />
            </button>
          </div>
        </div>

        {/* empty state */}
        {renderTurns?.length === 0 && !showPending && (
          <div className="reply-area">
            <div className="bdrs30 reply-area__top">
              <div className="rounded-circle logo-left">
                <img src="./assets/images/Group.png" alt="" />
              </div>
              <div className="text-content loading-text">
                {recordingStarted ? (
                  <i>Generating response...</i>
                ) : (
                  // <strong>You can ask any problem and we will help you.</strong>
                  <strong>
                    {" "}
                    Our AI tools help students understand better, improve their
                    learning.
                  </strong>
                )}
              </div>
            </div>
          </div>
        )}

        {/* all completed turns (FIX 1: renderTurns instead of displayTurns) */}
        {renderTurns?.map((turn, index) => {
          const isLastTurn = !showPending && index === renderTurns?.length - 1;
          const attachment = sentAttachments[index];

          return (
            <div
              key={turn.id ?? index}
              ref={isLastTurn ? lastTurnRef : null}
              className={`chat-turn ${isLastTurn ? "chat-turn--last" : ""}`}
            >
              <div className="bdrs20 user-row user-row--aira">
                {editingIndex === index ? (
                  <div className="position-relative w-100">
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="form-control fz20 input-with-buttons"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleEditSubmit();
                      }}
                    />
                    <div className="action-buttons-inside-input">
                      <button
                        className="border-0 btn btn-sm btn-light"
                        onClick={handleEditCancelBtn}
                      >
                        <img
                          src="./assets/images/x-circle-fill.svg"
                          alt="cancel"
                          className="rounded-full custom-icon"
                        />
                      </button>
                      <button
                        className="border-0 btn btn-sm btn-light"
                        onClick={handleEditSubmit}
                      >
                        <img
                          src="./assets/images/arrow-right-square-fill.svg"
                          alt="submit"
                          className="custom-icon"
                        />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <img
                      src="./assets/images/inner-pages/icons/icon-nav-user.svg"
                      alt=""
                    />
                    <p className="mb-0 text-white ff1 fz18 fw500">
                      {turn.question}
                    </p>
                    <button
                      className="ms-auto btn btn-round btn-round--edit"
                      onClick={() => {
                        setEditText(turn.question);
                        setEditingIndex(index);
                      }}
                    >
                      <img
                        src="./assets/images/inner-pages/icons/icon-edit.svg"
                        alt=""
                      />
                    </button>
                  </>
                )}
              </div>

              <div className="reply-area">
                <div className="bdrs30 reply-area__top">
                  <div className="rounded-circle logo-left">
                    <img src="./assets/images/Group.png" alt="" />
                  </div>
                  <div className="text-content" onClick={handleAnswerLinkClick}>
                    <MarkdownRenderer
                      markdown={formatMarkdownResponse(
                        turn.answer ||
                          (isLastTurn && isStreaming ? streamingText : ""),
                      )}
                    />
                  </div>
                </div>
                <div className="bdrs30 reply-area__bottom">
                  <div className="p-0 mt-auto bdrs30 actions">
                    <ul className="bdrs28 list-inline mb-0">
                      <SpeakerCopyButton
                        text={(turn.answer || "").trim()}
                        index={index}
                      />
                      <li>
                        <button className="btn btn-round btn-round--actions">
                          <img
                            src="./assets/images/inner-pages/icons/icon-thumbs-up.svg"
                            alt=""
                          />
                        </button>
                      </li>
                      <li>
                        <button className="btn btn-round btn-round--actions">
                          <img
                            src="./assets/images/inner-pages/icons/icon-thumbs-down.svg"
                            alt=""
                          />
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="cta-regenrate">
                    <button
                      className="btn btn-solid btn-solid--dark"
                      onClick={() => handleRegenerate(turn)}
                    >
                      <img
                        src="./assets/images/inner-pages/icons/icon-refresh.svg"
                        alt=""
                      />
                      <span className="ff2 fz16 text-nowrap fw500">
                        Regenerate Response
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* in-flight turn: only while waiting / streaming */}
        {showPending && (
          <div ref={lastTurnRef} className="chat-turn chat-turn--last">
            {/* FIX 2: user-row--pending aligns icon + question to the left */}
            <div className="bdrs20 user-row user-row--aira user-row--pending">
              <img
                src="./assets/images/inner-pages/icons/icon-nav-user.svg"
                alt=""
              />
              {/* FIX 1: use turnsAtSendRef.current (displayTurns.length can drop mid-stream) */}
              {sentAttachments[turnsAtSendRef.current] && (
                <div className="d-flex align-items-center gap-2 me-2">
                  <img
                    src={
                      sentAttachments[turnsAtSendRef.current].type === "image"
                        ? sentAttachments[turnsAtSendRef.current].url
                        : "./assets/images/inner-pages/icons/file-earmark-pdf.svg"
                    }
                    alt="attachment"
                    style={{
                      width: 32,
                      height: 32,
                      objectFit: "cover",
                      borderRadius: 6,
                    }}
                  />
                  <span
                    className="fz12 fw500 text-white text-truncate"
                    style={{ maxWidth: 140 }}
                  >
                    {sentAttachments[turnsAtSendRef.current].name}
                  </span>
                </div>
              )}
              <p className="mb-0 ff1 fz18 text-white fw500">
                {pendingQuestion}
              </p>
            </div>
            <div className="reply-area">
              <div className="bdrs30 reply-area__top">
                <div className="rounded-circle logo-left">
                  <img src="./assets/images/Group.png" alt="" />
                </div>
                <div className="text-content" onClick={handleAnswerLinkClick}>
                  {streamingText ? (
                    <MarkdownRenderer
                      markdown={formatMarkdownResponse(streamingText)}
                    />
                  ) : (
                    <div className="loading-text">
                      <i>Generating response...</i>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {uploadedFilePreview && (
          <div className="bdrs12 attachment-preview d-flex align-items-center gap-2 mb-2 p-2">
            {uploadedFilePreview?.type === "image" ? (
              <img
                src={uploadedFilePreview.url}
                alt="attachment"
                style={{
                  width: 40,
                  height: 40,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            ) : (
              <img
                src="./assets/images/inner-pages/icons/file-earmark-pdf.svg"
                alt="doc"
                style={{ width: 32, height: 32 }}
              />
            )}
            <span className="fz14 fw500 text-truncate">
              {uploadedFilePreview?.name}
              <button
                type="button"
                className="btn btn-sm border-0 ms-auto"
                onClick={handleRemoveAttachment}
              >
                <img
                  src="./assets/images/x-circle-fill.svg"
                  alt="remove"
                  className="custom-icon"
                />
              </button>
            </span>
          </div>
        )}
        {!streamingText && !showPending && (
          <div className="send-message-row">
            <div className="form-group">
              <img
                src="./assets/images/inner-pages/icons/icon-keyboard.svg"
                alt=""
                className="position-absolute icon-keyboard"
              />
              <input
                type="text"
                value={inputPrompt}
                onChange={(e) => setInputPrompt(e.target.value)}
                className="border-0 form-control fz16"
                placeholder="Ask me something..."
                disabled={isRecording}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && inputPrompt.trim())
                    handleSendPrompt();
                }}
              />

              {isRecording && (
                <div className="recording-indicator">
                  <span className="recording-dot" />
                  <span className="recording-time fz18 mx-3">
                    {/* {formatRecordingTime(recordingSeconds)} */}
                    {"Listening..."}
                  </span>
                </div>
              )}

              <div className="dropdown dropup file-down">
                <button
                  className="btn p-0 border-0 file-attach-btn dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img
                    src="./assets/images/inner-pages/icons/paperclip.svg"
                    alt="Attach"
                    className="custom-icon"
                  />
                </button>

                <div className="dropdown-menu">
                  <div
                    className="dropdown-item"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <img
                      src="./assets/images/inner-pages/icons/file-image.svg"
                      alt="Image Upload"
                      className="custom-icon"
                    />
                    <span className="">upload image</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={imageInputRef}
                    className="d-none"
                    onChange={handleImageUpload}
                  />

                  <div
                    className="dropdown-item"
                    onClick={() => docInputRef.current?.click()}
                  >
                    <img
                      src="./assets/images/inner-pages/icons/file-earmark-pdf.svg"
                      alt="Document Upload"
                      className="custom-icon"
                    />
                    <span className="">upload document</span>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    ref={docInputRef}
                    className="d-none"
                    onChange={handleDocUpload}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={isRecording ? stopRecording : startRecording}
              // className="btn btn-round btn-round--dark"
              className={`btn btn-round btn-round--dark ${isRecording ? "recording" : ""}`}
            >
              <img
                src={
                  isRecording
                    ? "./assets/images/inner-pages/icons/stop-circle.svg"
                    : "./assets/images/inner-pages/icons/icon-mic.svg"
                }
                alt="Mic"
              />
            </button>

            <button
              onClick={handleSendPrompt}
              disabled={isStreaming}
              className="btn btn-round btn-round--gray"
            >
              <img
                src="./assets/images/inner-pages/icons/icon-send.svg"
                alt="Send"
              />
            </button>
          </div>
        )}
      </div>
      <Loader show={loading} />
      <ToastMessage
        show={toastState.show}
        onClose={() => setToastState({ show: false, message: "", type: "" })}
        message={toastState.message}
        type={toastState.type}
      />
    </section>
  );
};

export default AiraChatBody;
